import { c as _c } from "react/compiler-runtime";
import { CheckCircleFilled } from '@ant-design/icons';
import { domAnimation, LazyMotion, m } from 'framer-motion';
import { useAtom } from 'jotai';
import { flowAtom, type Interval } from '@/features/payments/subscription/shared';
import { classNames } from '@/util/utils';
import { cn } from '@/utils/css-class';
import type { ReactNode } from 'react';
type Props = {
  id: string;
  title: ReactNode;
  price: number;
  currency: string;
  interval: Interval;
  discount?: number;
  selectedInterval: Interval | null;
  onSelect: (interval: Interval) => void;
};
function PricingCard(t0) {
  const $ = _c(35);
  const {
    id,
    title,
    price,
    currency,
    interval,
    discount,
    selectedInterval,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== interval || $[1] !== onSelect) {
    t1 = () => onSelect(interval);
    $[0] = interval;
    $[1] = onSelect;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== interval || $[4] !== selectedInterval) {
    t2 = cn("relative flex grow flex-col items-start rounded-2xl p-6", {
      "bg-primary-8 text-white! shadow-md border border-primary-8": selectedInterval === interval
    }, {
      "text-primary-8 bg-white border border-gray-200": selectedInterval !== interval
    });
    $[3] = interval;
    $[4] = selectedInterval;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = selectedInterval === interval;
  let t4;
  if ($[6] !== interval || $[7] !== t3) {
    t4 = <div className="flex w-full items-center justify-between"><input hidden={true} readOnly={true} className="border-border-200 size-[22px] bg-transparent" type="radio" name="billing_cycle" checked={t3} value={interval} /></div>;
    $[6] = interval;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== interval || $[10] !== selectedInterval) {
    t5 = selectedInterval === interval && <CheckCircleFilled className="text-green-400!" />;
    $[9] = interval;
    $[10] = selectedInterval;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5 || $[13] !== title) {
    t6 = <span className="flex items-center gap-2 text-2xl font-semibold">{title}{t5}</span>;
    $[12] = t5;
    $[13] = title;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== currency) {
    t7 = <small className="mr-1 text-sm font-light">{currency}</small>;
    $[15] = currency;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== interval) {
    t8 = <span className="font-light">/<span className="text-sm font-light">{interval}</span></span>;
    $[17] = interval;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== price || $[20] !== t8) {
    t9 = <span className="text-xl font-semibold">{price}{t8}</span>;
    $[19] = price;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t7 || $[23] !== t9) {
    t10 = <span className="inline-block text-sm">{t7}{t9}</span>;
    $[22] = t7;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== discount) {
    t11 = discount && <div className={classNames("text-primary-7 flex h-max items-center justify-center rounded-full bg-green-400 px-1 text-sm", "absolute top-2 right-4 p-1 px-3")}>Save {discount}-</div>;
    $[25] = discount;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== id || $[28] !== t1 || $[29] !== t10 || $[30] !== t11 || $[31] !== t2 || $[32] !== t4 || $[33] !== t6) {
    t12 = <m.button layout={true} id={id} data-testid="price-card" onClick={t1} type="button" className={t2}>{t4}{t6}{t10}{t11}</m.button>;
    $[27] = id;
    $[28] = t1;
    $[29] = t10;
    $[30] = t11;
    $[31] = t2;
    $[32] = t4;
    $[33] = t6;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
}
export default function PricingToggleCards() {
  const $ = _c(8);
  const [t0, updateFlowAtom] = useAtom(flowAtom);
  const {
    tier,
    interval
  } = t0;
  let t1;
  if ($[0] !== updateFlowAtom) {
    t1 = t => updateFlowAtom(prev => ({
      ...prev,
      interval: t
    }));
    $[0] = updateFlowAtom;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSelect = t1;
  if (!tier) {
    return null;
  }
  let t2;
  if ($[2] !== handleSelect || $[3] !== interval || $[4] !== tier) {
    t2 = tier?.prices?.map(o => <PricingCard key={`${o.id}`} id={o.id} title={tier.title} price={o.discount / 100 || o.amount / 100} currency={o.currency} interval={o.interval} discount={o.discount / 100} selectedInterval={interval} onSelect={handleSelect} />);
    $[2] = handleSelect;
    $[3] = interval;
    $[4] = tier;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <LazyMotion features={domAnimation}><div data-testid="price-cards" className="flex w-full flex-row items-center justify-center gap-3">{t2}</div></LazyMotion>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}